import { Task } from '@awork/features/task/models/task.model'
import { stringToDate } from '@awork/_shared/functions/date-operations'
import { isSameDay } from '@awork/_shared/functions/date-fns-wrappers'
import { CustomFieldDefinitionQuery } from '@awork/features/project/state/custom-field-definition.query'
import { CustomFieldTypeValue } from '@awork/features/project/models/custom-field-type.model'
import type { CustomField } from '@awork/features/project/models/custom-field.model'

/**
 * Filter used for search by name, tags and assigned user
 * @param {Task} task
 * @param {string} searchQuery
 * @returns {boolean}
 */
export function taskSearchFilter(task: Task, searchQuery: string): boolean {
  if (!searchQuery) {
    return true
  }

  if (!(task instanceof Task)) {
    task = Task.mapTask(task)
  }

  const search = searchQuery.toLowerCase()
  const isPrioTranslation = q.translations.common.isPrio?.toLowerCase()
  const prioStrings = ['prio', 'priority', isPrioTranslation]
  const searchDate = stringToDate(searchQuery)

  return matchesSearchCriteria(task, search, searchDate, prioStrings)
}

/**
 * Checks if the task matches the search criteria.
 * @param {Task} task
 * @param {string} search
 * @param {Date | undefined} searchDate
 * @param {string[]} prioStrings
 * @returns {boolean}
 */
function matchesSearchCriteria(
  task: Task,
  search: string,
  searchDate: Date | undefined,
  prioStrings: string[]
): boolean {
  if (task.name.toLowerCase().includes(search) || task.hasAssigneeWithName(search)) {
    return true
  }

  if (matchesTaskTags(task, search) || matchesTaskProject(task, search)) {
    return true
  }

  if (matchesTaskLists(task, search) || matchesTaskPriority(task, search, prioStrings)) {
    return true
  }

  if (task.taskStatus?.name?.toLowerCase().includes(search)) {
    return true
  }

  if (matchesTaskIdentifier(task, search)) {
    return true
  }

  if (matchesTaskDescription(task, search)) {
    return true
  }

  if (matchesTaskCustomFields(task, search, searchDate)) {
    return true
  }

  return matchesTaskDueDate(task, searchDate)
}

/**
 * Checks if the task tags match the search string.
 * @param {Task} task
 * @param {string} search
 * @returns {boolean}
 */
function matchesTaskTags(task: Task, search: string): boolean {
  return task.tags && task.tags.some(tag => tag.name.toLowerCase().includes(search))
}

/**
 * Checks if the task project matches the search string.
 * @param {Task} task
 * @param {string} search
 * @returns {boolean}
 */
function matchesTaskProject(task: Task, search: string): boolean {
  return (
    task.project?.name?.toLowerCase().includes(search) || task.project?.company?.name?.toLowerCase().includes(search)
  )
}

/**
 * Checks if the task lists match the search string.
 * @param {Task} task
 * @param {string} search
 * @returns {boolean}
 */
function matchesTaskLists(task: Task, search: string): boolean {
  return task.lists && task.lists.some(list => list.name?.toLowerCase().includes(search))
}

/**
 * Checks if the task priority matches the search string.
 * @param {Task} task
 * @param {string} search
 * @param {string[]} prioStrings
 * @returns {boolean}
 */
function matchesTaskPriority(task: Task, search: string, prioStrings: string[]): boolean {
  return task.isPrio && prioStrings.includes(search)
}

/**
 * Checks if the task due date matches the search date.
 * @param {Task} task
 * @param {Date | undefined} searchDate
 * @returns {boolean}
 */
function matchesTaskDueDate(task: Task, searchDate: Date | undefined): boolean {
  return !!(task.dueOn && searchDate && isSameDay(task.dueOn, searchDate))
}

/**
 * Checks if the task identifier matches the search string.
 * @param {Task} task
 * @param {string} search
 * @returns {boolean}
 */
function matchesTaskIdentifier(task: Task, search: string): boolean {
  return task.taskIdentifier?.toLowerCase().includes(search)
}

/**
 * Checks if the task description contains the search string.
 * The HTML of the description is stripped.
 * @param {Task} task
 * @param {string} search
 * @returns {boolean}
 */
function matchesTaskDescription(task: Task, search: string): boolean {
  if (!task.description) {
    return false
  }

  const description = task.description.replace(/<[^>]*>/g, '')

  return description.toLowerCase().includes(search)
}

/**
 * Checks if any custom field value contains the search string.
 * @param {Task} task
 * @param {string} search
 * @param {Date | undefined} searchDate
 * @returns {boolean}
 */
function matchesTaskCustomFields(task: Task, search: string, searchDate: Date | undefined): boolean {
  if (!task.customFields?.length) {
    return false
  }

  const definitionTypes = new Map<string, CustomFieldTypeValue>()

  if (CustomFieldDefinitionQuery.instance) {
    for (const field of task.customFields) {
      const definition = CustomFieldDefinitionQuery.instance.queryCustomFieldDefinition(field.customFieldDefinitionId)
      const type = definition()?.type

      if (type) {
        definitionTypes.set(field.customFieldDefinitionId, type)
      }
    }
  }

  return task.customFields.some(field =>
    matchesCustomFieldValue(field, search, searchDate, definitionTypes.get(field.customFieldDefinitionId))
  )
}

/**
 * Checks if a single custom field value matches the search criteria.
 * @param {CustomField} field
 * @param {string} search
 * @param {Date | undefined} searchDate
 * @param {CustomFieldTypeValue | undefined} fieldType
 * @returns {boolean}
 */
// eslint-disable-next-line complexity
function matchesCustomFieldValue(
  field: CustomField,
  search: string,
  searchDate: Date | undefined,
  fieldType: CustomFieldTypeValue | undefined
): boolean {
  if (field.textValue?.toLowerCase().includes(search)) {
    return true
  }

  if (field.selectionOptionIdValue) {
    const selectionOption = (field as { selectionOption?: { value?: string } }).selectionOption

    if (selectionOption?.value?.toLowerCase().includes(search)) {
      return true
    }
  }

  if (field.numberValue !== undefined && field.numberValue !== null) {
    if (field.numberValue.toString().includes(search)) {
      return true
    }
  }

  if (field.dateValue && searchDate && isSameDay(field.dateValue, searchDate)) {
    return true
  }

  if (fieldType === CustomFieldTypeValue.User && field.userIdValue) {
    if (field.selectedUser?.fullName?.toLowerCase().includes(search)) {
      return true
    }
  }

  if (fieldType === CustomFieldTypeValue.Client && field.clientIdValue) {
    if (field.selectedClient?.name?.toLowerCase().includes(search)) {
      return true
    }
  }

  return false
}
